import { trackProductAction } from '@/libs/product-analytics';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { invalidateDashboardOverview } from '@/hooks/queries/dashboard-overview-keys';
import axiosInstance from '@/libs/axiosInstance';
import { ErrorCode, type ApiResponse } from '@fluenceur/types';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface CommissionPayload {
  affiliateId: string;
}

interface CreatePayoutPayload {
  commissions: CommissionPayload[];
}

interface CreatePayoutResponse {
  id: string;
  paymentIntentClientSecret: string;
}

const createPayout = async (payload: CreatePayoutPayload): Promise<CreatePayoutResponse> => {
  const { data } = await axiosInstance.post<CreatePayoutResponse>('/dashboard/payouts', payload);
  return data;
};

export const useCreatePayoutAction = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation<CreatePayoutResponse, AxiosError<ApiResponse>, CreatePayoutPayload>({
    mutationFn: (input) => trackProductAction('payments', 'create', () => createPayout(input)),
    onSuccess: (data) => {
      // The commissions this payout holds leave the dashboard's to-do card.
      invalidateDashboardOverview(queryClient);
      // Redirect on success, passing the client secret in state
      navigate(`/payout/${data.id}/checkout`, {
        state: { paymentIntentClientSecret: data.paymentIntentClientSecret },
      });
    },
    onError: (error) => {
      const responseData = error.response?.data;
      const isIneligibleRecipientError =
        responseData?.code === ErrorCode.E_PAYOUT_BATCH_HAS_INELIGIBLE_RECIPIENTS;
      const isNoPayableCommissionsError = responseData?.code === 'E_NO_PENDING_COMMISSIONS';

      if (isNoPayableCommissionsError) {
        // Eligibility changed after this list was read. Read every commission
        // surface again so the amounts and review state come from the server.
        void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      }
      const description = isNoPayableCommissionsError
        ? t('commissions.pending.paymentError.noPayable')
        : isIneligibleRecipientError
          ? t('commissions.pending.paymentError.ineligibleRecipients')
          : (responseData?.message ?? t('commissions.pending.paymentError.generic'));

      toaster.create({
        title: t('commissions.pending.paymentError.title'),
        description,
        type: 'error',
      });
    },
  });

  return mutation;
};
